import {
  AdapterConfigurationError,
  AdapterHttpError,
  AdapterValidationError,
  ValidatedHttpMarketDataAdapter,
  parseProductionDataEnvelope,
  type DataCoverage,
  type ProductionDataEnvelope,
} from "../data";
import type { ISODate, PairPresetId } from "../domain";
import { createDashboardApplication } from "./loader";
import type {
  DashboardAnalysisRequest,
  DashboardCurrentKnowledgeResult,
  DashboardPairCatalogEntry,
} from "./types";

const PRODUCTION_API_PATH = "/api/market-data/";
const PRODUCTION_BOOTSTRAP_PATH = "v1/bootstrap";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface BrowserProductionLoaderConfig {
  /** Test/SSR override. Browser callers should omit this and use location.origin. */
  readonly origin?: string;
  readonly fetch?: FetchLike;
}

export interface BrowserProductionMetadata {
  readonly mode: "production";
  readonly source: ProductionDataEnvelope["source"];
  readonly asOf: ISODate;
  readonly generatedAt: string;
  readonly coverage: DataCoverage;
  readonly knowledgeScope: "current-knowledge";
}

export interface BrowserProductionDashboardApplication {
  readonly mode: "production";
  readonly apiBaseUrl: string;
  readonly metadata: BrowserProductionMetadata;
  readonly pairs: readonly DashboardPairCatalogEntry[];
  readonly pairCatalog: Readonly<Record<PairPresetId, DashboardPairCatalogEntry>>;
  analyze(request: DashboardAnalysisRequest): Promise<DashboardCurrentKnowledgeResult>;
  loadPairCatalog(asOf: ISODate): Promise<readonly DashboardPairCatalogEntry[]>;
}

/**
 * Browser-only production bootstrap. Every failure is terminal to production
 * initialization; this path never imports or falls back to the demo fixture.
 */
export async function loadBrowserProductionDashboardApplication(
  config: BrowserProductionLoaderConfig = {},
): Promise<BrowserProductionDashboardApplication> {
  const fetchImpl = config.fetch ?? globalThis.fetch?.bind(globalThis);
  if (!fetchImpl) {
    throw new AdapterConfigurationError("No Fetch implementation is available for production bootstrap");
  }
  const origin = resolveBrowserOrigin(config.origin);
  const apiBaseUrl = new URL(PRODUCTION_API_PATH, origin);
  const bootstrapUrl = new URL(PRODUCTION_BOOTSTRAP_PATH, apiBaseUrl);
  const envelope = await fetchProductionBootstrap(fetchImpl, bootstrapUrl);
  const adapter = new ValidatedHttpMarketDataAdapter({
    baseUrl: apiBaseUrl.href,
    fetch: fetchImpl,
  });
  const coverage = await adapter.getCoverage();
  if (coverage.sourceId !== envelope.source.id) {
    throw new AdapterValidationError(
      `Production bootstrap source ${envelope.source.id} does not match coverage source ${coverage.sourceId}`,
    );
  }
  const service = createDashboardApplication({
    mode: "production",
    envelope,
    adapter,
  });
  const asOf = envelope.asOfDate as ISODate;
  const pairs = await service.loadPairCatalog(asOf);
  const pairCatalog = Object.fromEntries(
    pairs.map((pair) => [pair.id, pair]),
  ) as Record<PairPresetId, DashboardPairCatalogEntry>;

  return {
    mode: "production",
    apiBaseUrl: apiBaseUrl.href,
    metadata: {
      mode: "production",
      source: envelope.source,
      asOf,
      generatedAt: envelope.generatedAt,
      coverage,
      knowledgeScope: "current-knowledge",
    },
    pairs,
    pairCatalog,
    analyze: (request) => service.analyzeCurrentKnowledge(request),
    loadPairCatalog: (nextAsOf) => service.loadPairCatalog(nextAsOf),
  };
}

function resolveBrowserOrigin(override: string | undefined): URL {
  const value = override ?? globalThis.location?.origin;
  if (!value) {
    throw new AdapterConfigurationError(
      "Browser origin is unavailable; production API must use the same origin",
    );
  }
  let origin: URL;
  try {
    origin = new URL(value);
  } catch (error) {
    throw new AdapterConfigurationError("Browser origin is invalid", { cause: error });
  }
  if (!new Set(["http:", "https:"]).has(origin.protocol)) {
    throw new AdapterConfigurationError("Browser origin must use HTTP or HTTPS");
  }
  return origin;
}

async function fetchProductionBootstrap(
  fetchImpl: FetchLike,
  url: URL,
): Promise<ProductionDataEnvelope> {
  let response: Response;
  try {
    response = await fetchImpl(url, {
      headers: { accept: "application/json" },
    });
  } catch (error) {
    throw new AdapterHttpError(0, "Production bootstrap request failed", { cause: error });
  }
  if (!response.ok) {
    throw new AdapterHttpError(response.status, `Production bootstrap returned ${response.status}`);
  }
  let value: unknown;
  try {
    value = await response.json();
  } catch (error) {
    throw new AdapterValidationError("Production bootstrap returned invalid JSON", { cause: error });
  }
  try {
    return parseProductionDataEnvelope(value);
  } catch (error) {
    throw new AdapterValidationError("Production bootstrap failed production-envelope validation", {
      cause: error,
    });
  }
}
